import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArmadaNav } from "@/components/ArmadaNav";
import { RoleBadge } from "@/components/Badges";
import {
  Heart,
  CreditCard,
  Activity,
  Vault,
  CheckCircle2,
  AlertCircle,
  ArrowDownToLine,
  Anchor,
} from "lucide-react";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

function StatusPill({ status }) {
  const map = {
    DORMANT: { bg: "bg-zinc-500/15", border: "border-zinc-500/40", text: "text-zinc-300", label: "DORMANT" },
    READY: { bg: "bg-sky-500/15", border: "border-sky-400/40", text: "text-sky-200", label: "READY" },
    REPAYING: { bg: "bg-amber-500/15", border: "border-amber-400/40", text: "text-amber-200", label: "REPAYING" },
    REPAID: { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-200", label: "REPAID" },
  };
  const s = map[status] || map.DORMANT;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-wider border ${s.bg} ${s.border} ${s.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.text.replace("text", "bg")}`} />
      {s.label}
    </span>
  );
}

export default function ParentalBridgePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/guardian/bridge");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const av = data?.activation_vault;
  const ov = data?.oversight;

  return (
    <div className="min-h-screen bg-armada-base relative">
      <div className="absolute inset-0 dashboard-grid opacity-20 pointer-events-none" />
      <div className="relative">
        <ArmadaNav />
        <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
          <div className="mb-8 animate-fade-up">
            <div className="text-[10px] tracking-armada text-purple-300 font-bold mb-2">
              PARENTAL BRIDGE
            </div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
              Guardian, <span className="gold-shimmer">{user?.full_name?.split(" ")[0] || ""}</span>
            </h1>
            <div className="flex items-center gap-3 mt-3">
              <RoleBadge role={user?.role} />
              {ov?.child_name && (
                <span className="text-xs tracking-wider text-zinc-500">
                  · Oversight ·{" "}
                  <span className="text-rose-300 font-semibold">{ov.child_name}</span>
                </span>
              )}
            </div>
          </div>

          {err && (
            <div
              data-testid="guardian-error"
              className="mb-6 p-4 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm"
            >
              {err}
            </div>
          )}

          {data && (
            <>
              {/* ─── ACTIVATION VAULT ─── */}
              <div
                data-testid="activation-vault"
                className="glass-strong rounded-2xl p-8 mb-8 border border-purple-400/20 relative overflow-hidden"
              >
                <div
                  className="absolute inset-0 opacity-10 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle at 20% 30%, rgba(168,85,247,0.5), transparent 60%)",
                  }}
                />
                <div className="relative">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Vault className="w-5 h-5 text-purple-300" />
                        <div className="text-[10px] tracking-armada text-purple-300 font-bold">
                          ACTIVATION VAULT
                        </div>
                        <StatusPill status={av?.status} />
                      </div>
                      <div className="text-sm text-zinc-400 max-w-xl">
                        {av?.hull_activated ? (
                          <>
                            <span className="text-white font-semibold">
                              {av.activated_hull || "Hull"}
                            </span>{" "}
                            is live. Your share: <span className="text-purple-200 font-bold">65%</span>
                            {" "}— but until the Advance is repaid in full, every cent of your share is
                            routed to debt settlement.
                          </>
                        ) : (
                          <>
                            Hull dormant. Director K must call{" "}
                            <code className="text-purple-200">/api/admin/guardian/.../advance</code>{" "}
                            with your build credit to activate.
                          </>
                        )}
                      </div>
                    </div>
                    <div className="px-4 py-2 rounded-md border border-purple-400/30 bg-purple-400/5">
                      <div className="text-[9px] tracking-armada text-purple-300/80 mb-1">
                        REVENUE LAW
                      </div>
                      <div className="text-xs text-white font-bold tabular-nums">
                        65% Guardian / 25% Fleet / 10% CyberKids
                      </div>
                    </div>
                  </div>

                  {/* Advance Balance vs Repayment Pulse */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div
                      data-testid="advance-balance"
                      className="p-6 rounded-xl border border-amber-500/30 bg-amber-500/5"
                    >
                      <div className="flex items-center gap-2 text-[10px] tracking-armada text-amber-300 font-bold mb-3">
                        <CreditCard className="w-4 h-4" /> ADVANCE BALANCE
                      </div>
                      <div className="text-4xl md:text-5xl font-black text-white tracking-tighter">
                        {fmtUSD(av?.advance_balance_usd)}
                      </div>
                      <div className="text-xs text-zinc-500 mt-2">
                        outstanding of {fmtUSD(av?.advance_original_usd)} original
                      </div>
                    </div>

                    <div
                      data-testid="repayment-pulse"
                      className="p-6 rounded-xl border border-purple-400/30 bg-purple-500/5"
                    >
                      <div className="flex items-center gap-2 text-[10px] tracking-armada text-purple-300 font-bold mb-3">
                        <Activity className="w-4 h-4" /> REPAYMENT PULSE
                      </div>
                      <div className="text-4xl md:text-5xl font-black text-white tracking-tighter">
                        {av?.repayment_pct?.toFixed?.(2) ?? "0.00"}
                        <span className="text-2xl text-zinc-500">%</span>
                      </div>
                      <div className="mt-3 h-2 rounded-full bg-black/40 overflow-hidden">
                        <div
                          data-testid="repayment-pulse-bar"
                          className="h-full bg-gradient-to-r from-purple-500 to-amber-400 transition-all duration-700"
                          style={{
                            width: `${Math.min(100, av?.repayment_pct || 0)}%`,
                          }}
                        />
                      </div>
                      <div className="text-xs text-zinc-500 mt-2">
                        {av?.repayment_pulse_count || 0} repayment pulses ·{" "}
                        {fmtUSD(av?.advance_repaid_usd)} repaid
                      </div>
                    </div>

                    <div
                      data-testid="payout-balance"
                      className={`p-6 rounded-xl border ${
                        av?.fully_repaid
                          ? "border-emerald-500/40 bg-emerald-500/5"
                          : "border-zinc-700 bg-black/20"
                      }`}
                    >
                      <div
                        className={`flex items-center gap-2 text-[10px] tracking-armada font-bold mb-3 ${
                          av?.fully_repaid ? "text-emerald-300" : "text-zinc-500"
                        }`}
                      >
                        {av?.fully_repaid ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <AlertCircle className="w-4 h-4" />
                        )}
                        PAYOUT BALANCE · YOUR 65%
                      </div>
                      <div className="text-4xl md:text-5xl font-black text-white tracking-tighter">
                        {fmtUSD(av?.payout_balance_usd)}
                      </div>
                      <div className="text-xs text-zinc-500 mt-2">
                        {av?.fully_repaid
                          ? "Advance cleared. Your share now accrues."
                          : "Locked until Advance is repaid 100%"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── OVERSIGHT — CHILD'S VAULT ─── */}
              <div
                data-testid="oversight-card"
                className="glass-strong rounded-2xl p-8 mb-8 border border-rose-400/20"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Heart className="w-4 h-4 text-rose-300" />
                  <div className="text-[10px] tracking-armada text-rose-300 font-bold">
                    OVERSIGHT · {ov?.child_name?.toUpperCase()} · HEIR VAULT
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                  <div className="p-5 rounded-xl border border-amber-500/20 bg-amber-500/5">
                    <div className="text-[10px] tracking-armada text-amber-300 font-bold mb-2">
                      ANCESTRAL POOL
                    </div>
                    <div
                      data-testid="child-ancestral"
                      className="text-3xl font-black text-white tracking-tighter"
                    >
                      {fmtUSD(ov?.child_vault_usd?.ancestral_balance)}
                    </div>
                  </div>
                  <div className="p-5 rounded-xl border border-emerald-500/20 bg-emerald-500/5">
                    <div className="text-[10px] tracking-armada text-emerald-300 font-bold mb-2">
                      LIVE HEIR TAX
                    </div>
                    <div
                      data-testid="child-live"
                      className="text-3xl font-black text-white tracking-tighter"
                    >
                      {fmtUSD(ov?.child_vault_usd?.live_balance)}
                    </div>
                  </div>
                </div>

                <div className="text-[10px] tracking-armada text-zinc-500 mb-3">
                  RECENT CHILD VAULT CREDITS
                </div>
                {ov?.child_recent_ledger?.length === 0 ? (
                  <div className="text-sm text-zinc-500 py-4 text-center border border-dashed border-white/10 rounded-md">
                    No credits yet for {ov?.child_name}.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(ov?.child_recent_ledger || []).slice(0, 8).map((row, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 rounded-md bg-black/30 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              row.bucket === "ancestral" ? "bg-amber-400" : "bg-emerald-400"
                            }`}
                          />
                          <div className="text-xs">
                            <div className="text-white font-semibold">
                              {row.bucket === "ancestral" ? "Ancestral" : "Live"} credit
                            </div>
                            <div className="text-[10px] tracking-wider text-zinc-500">
                              {row.source_channel} ·{" "}
                              {new Date(row.credited_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-bold text-white tabular-nums">
                          +{fmtUSD((row.amount_cents || 0) / 100)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ─── REPAYMENT PULSE LOG ─── */}
              <div className="glass-strong rounded-xl p-6 border border-white/5">
                <div className="flex items-center gap-2 mb-5">
                  <ArrowDownToLine className="w-4 h-4 text-purple-300" />
                  <div className="text-[10px] tracking-armada text-purple-300 font-bold">
                    REPAYMENT PULSE LOG · LAST 20
                  </div>
                </div>
                {data?.repayment_pulse?.length === 0 ? (
                  <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-white/10 rounded-md">
                    No repayment pulses yet. Activate your hull and start a checkout to fire the
                    first pulse.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.repayment_pulse.map((p, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 rounded-md bg-black/30 border border-white/5"
                      >
                        <div className="flex items-center gap-3">
                          <Anchor className="w-3.5 h-3.5 text-purple-300" />
                          <div className="text-xs">
                            <div className="text-white font-semibold tabular-nums">
                              {fmtUSD((p.amount_cents || 0) / 100)} repaid
                            </div>
                            <div className="text-[10px] tracking-wider text-zinc-500">
                              {new Date(p.credited_at).toLocaleString()}
                              {p.fully_repaid && (
                                <span className="ml-2 text-emerald-300 font-bold">
                                  · ADVANCE CLEARED
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-[10px] tracking-wider text-zinc-500">
                          balance · {fmtUSD((p.balance_after_cents || 0) / 100)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
